import React, { FC, useEffect, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import TextField from "@mui/material/TextField";
import Select from "@/components/Select/index";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid2";
import Box from "@mui/material/Box";
import Datepicker from "@/components/Datepicker";
import { CreateProgramFormData } from "./validation.schema";
import dayjs from "dayjs";
import { useSearchRoles } from "@/hooks/management/useRoles.manage";
import { AutocompleteWithChips } from "@/components/AutocompleteWithChips/AutocompleteWithChips";
import {
  IncentiveStructureEnum,
  ProgramStatusEnum,
  YesNoEnum,
} from "@/domain/programs.enum";
import { FormSection } from "@/ui/sections/FormSection";

type TabProgramGeneralProps = {
  editMode?: boolean;
};

export const TabProgramGeneral: FC<TabProgramGeneralProps> = ({
  editMode = false,
}) => {
  const { t } = useTranslation("program");
  const { t: tCommon } = useTranslation("common");

  const {
    control,
    setValue,
    formState: { errors },
    watch,
  } = useFormContext<CreateProgramFormData>();

  const statusOptions = useMemo(() => {
    return Object.values(ProgramStatusEnum).map((status) => ({
      key: status,
      value: tCommon(`enums.${status}` as any),
    }));
  }, []);

  const yesNoOptions = useMemo(() => {
    return Object.values(YesNoEnum).map((it) => ({
      key: it,
      value: tCommon(`enums.${it}` as any),
    }));
  }, []);

  const incentiveStructureOptions = useMemo(() => {
    return Object.values(IncentiveStructureEnum).map((it) => ({
      key: it,
      value: t(`enums.${it}` as any),
    }));
  }, []);

  const { rolesResponse } = useSearchRoles({
    sortField: "name",
    sortOrder: 1,
    pageNumber: 0,
    pageSize: 100,
    search: "",
  });

  const cmRolesWatched = watch("cmRoles") as string[];
  const additionalStaffRolesWatched = watch("additionalStaffRoles") as string[];
  const incentiveOnDemandWatched = watch(
    "enableOnDemandIncentive",
  ) as YesNoEnum;
  const programDurationWatched = watch("programDuration");

  // Auto-calculate programDays = programDuration (weeks) * 7
  useEffect(() => {
    const raw = programDurationWatched as unknown;
    const weeksStr =
      raw == null ? "" : typeof raw === "number" ? String(raw) : String(raw);
    if (weeksStr.trim() === "") {
      setValue("programDays", "", { shouldValidate: true, shouldDirty: true });
      return;
    }
    const weeks = Number(weeksStr);
    if (!Number.isFinite(weeks)) {
      setValue("programDays", "", { shouldValidate: true, shouldDirty: true });
      return;
    }
    setValue("programDays", weeks * 7, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [programDurationWatched, setValue]);

  const cmRolesOptions = useMemo(() => {
    if (!rolesResponse?.roles) return [];
    return rolesResponse.roles
      .map((role) => ({
        key: role._id,
        value: role.name,
      }))
      .filter(
        (role) => (additionalStaffRolesWatched || []).indexOf(role.key) === -1,
      );
  }, [rolesResponse, additionalStaffRolesWatched]);

  const additionalStaffRolesOptions = useMemo(() => {
    if (!rolesResponse?.roles) return [];
    return rolesResponse.roles
      .map((role) => ({
        key: role._id,
        value: role.name,
      }))
      .filter((role) => (cmRolesWatched || []).indexOf(role.key) === -1);
  }, [rolesResponse, cmRolesWatched]);

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 4 }}>
        <FormSection title={t("sections.general-information")}>
          <Controller
            name="name"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="name"
                label={t("labels.name")}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.name}
                helperText={errors.name ? errors.name.message : null}
                fullWidth
                required={true}
              />
            )}
          />
          <Controller
            name="status"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Select
                handleChange={(value) => {
                  field.onChange(value);
                }}
                data={statusOptions || []}
                value={(field.value as string[]) || []}
                label={t("labels.status")}
                id="status"
                fullWidth
                error={!!errors.status}
                errorMessage={errors.status?.message}
                disabled={!editMode}
                required={true}
              />
            )}
          />
          <Controller
            name="cmRoles"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <AutocompleteWithChips
                handleChange={(value) => {
                  field.onChange(value);
                }}
                options={cmRolesOptions || []}
                selectedValues={(field.value as string[]) || []}
                label={t("labels.cm-roles")}
                id="cmRoles"
                error={!!errors.cmRoles}
                errorMessage={errors.cmRoles?.message}
                required={true}
              />
            )}
          />
          <Controller
            name="additionalStaffRoles"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <AutocompleteWithChips
                handleChange={(value) => {
                  field.onChange(value);
                }}
                options={additionalStaffRolesOptions || []}
                selectedValues={(field.value as string[]) || []}
                label={t("labels.additional-staff-roles")}
                id="additional-staff-roles"
                error={!!errors.additionalStaffRoles}
                errorMessage={errors.additionalStaffRoles?.message}
                required={false}
              />
            )}
          />
        </FormSection>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <FormSection title={t("sections.program-numbers")}>
          <Controller
            name="maxParticipants"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="max-number-participants"
                label={t("labels.max-number-participants")}
                variant="outlined"
                value={field.value}
                onChange={(value) => {
                  const inputValue = value.target.value;
                  if (/^\d*$/.test(inputValue)) {
                    field.onChange(value);
                  }
                }}
                error={!!errors.maxParticipants}
                helperText={
                  errors.maxParticipants ? errors.maxParticipants.message : null
                }
                fullWidth
                type="number"
                inputProps={{ step: 1 }}
              />
            )}
          />
          <Controller
            name="programDuration"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="program-duration"
                label={t("labels.program-duration")}
                variant="outlined"
                value={field.value}
                onChange={(value) => {
                  const inputValue = value.target.value;
                  if (/^\d*$/.test(inputValue)) {
                    field.onChange(value);
                  }
                }}
                error={!!errors.programDuration}
                helperText={
                  errors.programDuration ? errors.programDuration.message : null
                }
                fullWidth
                type="number"
              />
            )}
          />
          <Controller
            name="programBudgetLimit"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="program-budget-limit"
                label={t("labels.program-budget-limit")}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.programBudgetLimit}
                helperText={
                  errors.programBudgetLimit
                    ? errors.programBudgetLimit.message
                    : null
                }
                fullWidth
                type="number"
              />
            )}
          />
        </FormSection>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <FormSection title={t("sections.program-parameters")}>
          <Controller
            name="eligibilityConfirmation"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Select
                handleChange={(value) => field.onChange(value)}
                data={yesNoOptions || []}
                value={(field.value as string) || ""}
                label={t("labels.requires-eligibility-confirmation")}
                id="requires-eligibility-confirmation"
                fullWidth
                error={!!errors.eligibilityConfirmation}
                errorMessage={errors.eligibilityConfirmation?.message}
                required={true}
              />
            )}
          />
          <Controller
            name="consentForm"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Select
                handleChange={(value) => field.onChange(value)}
                data={yesNoOptions || []}
                value={(field.value as string) || ""}
                label={t("labels.requires-consent-form-upload")}
                id="requires-consent-form-upload"
                fullWidth
                error={!!errors.consentForm}
                errorMessage={errors.consentForm?.message}
                required={true}
              />
            )}
          />
          <Controller
            name="enableOnDemandIncentive"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Select
                handleChange={(value) => field.onChange(value)}
                data={yesNoOptions || []}
                value={(field.value as string) || ""}
                label={t("labels.incentive-on-demand")}
                id="incentive-on-demand"
                fullWidth
                error={!!errors.enableOnDemandIncentive}
                errorMessage={errors.enableOnDemandIncentive?.message}
                required={true}
              />
            )}
          />
          <Box display="flex" gap={2}>
            <Controller
              name="minAmount"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  id="min-amount"
                  label={t("labels.min-amount")}
                  variant="outlined"
                  value={field.value}
                  onChange={(value) => field.onChange(value)}
                  error={!!errors.minAmount}
                  helperText={
                    errors.minAmount ? errors.minAmount.message : null
                  }
                  fullWidth
                  type="number"
                  required={incentiveOnDemandWatched === YesNoEnum.YES}
                />
              )}
            />
            <Controller
              name="maxAmount"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  id="max-amount"
                  label={t("labels.max-amount")}
                  variant="outlined"
                  value={field.value}
                  onChange={(value) => field.onChange(value)}
                  error={!!errors.maxAmount}
                  helperText={
                    errors.maxAmount ? errors.maxAmount.message : null
                  }
                  fullWidth
                  type="number"
                  required={incentiveOnDemandWatched === YesNoEnum.YES}
                />
              )}
            />
          </Box>
          <Controller
            name="maxEarnings"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="max-earnings"
                label={t("labels.max-earnings")}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.maxEarnings}
                helperText={
                  errors.maxEarnings ? errors.maxEarnings.message : null
                }
                fullWidth
                type="number"
              />
            )}
          />
          <Controller
            name="incentiveStructure"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Select
                handleChange={(value) => {
                  field.onChange(value);
                }}
                data={incentiveStructureOptions}
                value={(field.value as string) || ""}
                label={t("labels.incentive-structure")}
                id="incentive-structure"
                fullWidth
                error={!!errors.incentiveStructure}
                errorMessage={errors.incentiveStructure?.message}
                required={true}
              />
            )}
          />
          <Controller
            name="startDate"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Datepicker
                {...field}
                label={t("labels.program-start-date")}
                disableWrite={true}
                disableFuture={false}
                onChange={(value) => {
                  field.onChange(value ? value.format("YYYY-MM-DD") : "");
                }}
                value={
                  field.value
                    ? dayjs(field.value as string, "YYYY-MM-DD")
                    : null
                }
                error={!!errors.startDate}
                errorMessage={errors.startDate?.message}
                required={true}
              />
            )}
          />
          <Controller
            name="endDate"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Datepicker
                {...field}
                label={t("labels.program-end-date")}
                disableWrite={true}
                disableFuture={false}
                onChange={(value) => {
                  field.onChange(value ? value.format("YYYY-MM-DD") : "");
                }}
                value={
                  field.value
                    ? dayjs(field.value as string, "YYYY-MM-DD")
                    : null
                }
                error={!!errors.endDate}
                errorMessage={errors.endDate?.message}
              />
            )}
          />
          <Controller
            name="programDays"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                id="program-days"
                label={t("labels.program-days")}
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!errors.programDays}
                helperText={
                  errors.programDays ? errors.programDays.message : null
                }
                disabled={true}
                fullWidth
                type="number"
                required
                InputProps={{ readOnly: true }}
              />
            )}
          />
        </FormSection>
      </Grid>
    </Grid>
  );
};
